<template>
    <app-layout>
        <div v-if="isSuccess" class="success-container">
            <common-confirmation-icon type="success"></common-confirmation-icon>
            <p class="success-text">{{ $t('common.report_bug.support') }}</p>
        </div>
        <common-content-spacer v-else height="medium" class="main">
            <span>
                <h1 class="heading">{{ $t('common.report_bug.heading_main') }}</h1>
                <h2 class="heading_desc">{{ $t('common.report_bug.heading_desc') }}</h2>
            </span>
            <form-wrapper class="container" @submit.prevent="submitForm">
                <common-content-spacer height="medium">
                    <form-row>
                        <form-cell>
                            <text-field
                                :label="$t('forms.referrals.make.fields.physician_full_name.label')"
                                :placeholder="$t('forms.referrals.make.fields.physician_full_name.placeholder')"
                                :isRequired="true"
                                valuePath="fullName"
                                :formData="data"
                                @change="change"
                            ></text-field>
                        </form-cell>
                        <form-cell>
                            <text-field
                                :label="$t('forms.patient.patient_information.fields.email.label')"
                                :placeholder="$t('forms.patient.patient_information.fields.email.placeholder')"
                                :isRequired="true"
                                valuePath="email"
                                :formData="data"
                                @change="change"
                            ></text-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <text-field
                                :label="$t('common.report_bug.title')"
                                :placeholder="$t('common.report_bug.title_placeholder')"
                                :isRequired="true"
                                :formData="data"
                                valuePath="title"
                                @change="change($event)"
                            ></text-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <select-field
                                :label="$t('common.report_bug.priority')"
                                :placeholder="$t('common.report_bug.pri_placeholder')"
                                :options="priorityItems"
                                :isRequired="true"
                                :formData="data"
                                valuePath="priority"
                                @change="change($event)"
                            >
                                <template #hint>
                                    {{ $t('common.report_bug.priority_hint') }}
                                </template>
                            </select-field>
                        </form-cell>
                        <form-cell>
                            <select-field
                                :label="$t('common.report_bug.issue')"
                                :placeholder="$t('common.report_bug.issue_placeholder')"
                                :options="issueTypeItems"
                                :isRequired="true"
                                :formData="data"
                                valuePath="issueType"
                                @change="change($event)"
                            ></select-field>
                        </form-cell>
                    </form-row>

                    <form-row>
                        <form-cell>
                            <text-field
                                :label="$t('modals.patient_profile.profile.detail_form.document_detail.description')"
                                :placeholder="$t('common.report_bug.description_placeholder')"
                                :isRequired="true"
                                valuePath="description"
                                type="text"
                                :formData="data"
                                @change="change"
                            ></text-field>
                            <p class="drag-text">{{ $t('common.report_bug.description_tip') }}</p>
                        </form-cell>
                    </form-row>
                    <form-row>
                        <form-cell>
                            <label class="form-label">{{ $t('common.report_bug.upload_screenshot') }}</label>

                            <label for="fileInput" class="upload-box">
                                <div v-if="!selectedPhotos.length" class="upload-content">
                                    <svg class="upload">
                                        <use xlink:href="#icon-upload" />
                                    </svg>

                                    <p class="browse">
                                        {{ $t('common.report_bug.attachments') }}
                                    </p>
                                    <p class="drag-text">
                                        {{ $t('common.report_bug.attachments_text') }}
                                    </p>
                                </div>

                                <div v-else class="preview" @click.stop>
                                    <div v-for="(photo, index) in selectedPhotos" :key="index" class="preview-item">
                                        <button type="button" class="remove-btn" @click.stop.prevent="removePhoto(index)">
                                            ✕
                                        </button>
                                        <img :src="photo.preview" class="preview-image" />
                                        <span class="file-name">{{ photo.name }}</span>
                                    </div>
                                </div>

                                <input
                                    id="fileInput"
                                    type="file"
                                    class="fileInput"
                                    accept="image/*"
                                    multiple
                                    @change="onFileChange"
                                />
                            </label>
                        </form-cell>
                    </form-row>
                    <common-buttons>
                        <common-button
                            label="Submit"
                            type="primary"
                            size="large"
                            :isProcessing="isSubmitting"
                            :isDisabled="!canSubmit"
                            nativeType="submit"
                        ></common-button>
                    </common-buttons>
                </common-content-spacer>
            </form-wrapper>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import FormMixin from 'mixins/FormMixin'
    import SupportAPI from '../../api/SupportAPI'
    import CommonConfirmationIcon from '../../components/common/CommonConfirmationIcon.vue'

    export default {
        mixins: [FormMixin, PageWithDataMixin],
        components: { AppLayout, CommonConfirmationIcon },
        data() {
            return {
                isSubmitting: false,
                selectedPhotos: [],
                isSuccess: false,
            }
        },

        computed: {
            canSubmit() {
                return (
                    this.data.fullName &&
                    this.data.email &&
                    this.data.title &&
                    this.data.priority &&
                    this.data.issueType &&
                    this.data.description &&
                    !this.isSubmitting
                )
            },
            priorityItems() {
                return [
                    { value: 'Urgent', label: 'Urgent' },
                    { value: 'High', label: 'High' },
                    { value: 'Normal', label: 'Normal' },
                    { value: 'Low', label: 'Low' },
                ]
            },
            issueTypeItems() {
                return [
                    { value: 'Technical Issue', label: 'Technical Issue' },
                    { value: 'Billing & Account', label: 'Billing & Account' },
                    { value: 'Feature Request', label: 'Feature Request' },
                    { value: 'General Inquiry', label: 'General Inquiry' },
                    { value: 'Feedback', label: 'Feedback' },
                    { value: 'Other', label: 'Other' },
                ]
            },
        },

        methods: {
            buildFullName(member) {
                return [member.firstName, member.middleName, member.lastName].filter(Boolean).join(' ')
            },

            onFileChange(event) {
                const files = Array.from(event.target.files)

                files.forEach((file) => {
                    const reader = new FileReader()

                    reader.onload = () => {
                        this.selectedPhotos.push({
                            file,
                            name: file.name,
                            preview: reader.result,
                        })
                    }

                    reader.readAsDataURL(file)
                })

                event.target.value = ''
            },

            removePhoto(index) {
                this.selectedPhotos.splice(index, 1)
            },

            submitForm() {
                if (!this.canSubmit) return

                this.isSubmitting = true

                const payload = {
                    fullName: this.data.fullName,
                    email: this.data.email,
                    title: this.data.title,
                    priority: this.data.priority,
                    issueType: this.data.issueType,
                    description: this.data.description,
                }

                if (this.selectedPhotos.length) {
                    payload.photos = this.selectedPhotos.map((p) => p.preview)
                }

                SupportAPI.reportBug(payload).then(() => {
                    this.isSuccess = true
                    this.isSubmitting = false
                    this.resetForm()
                })
            },

            resetForm() {
                this.data = {}
                this.selectedPhotos = []
            },
        },
        mounted() {
            const member = this.$auth?.getActiveMember?.()

            if (member) {
                this.data = {
                    ...this.data,
                    fullName: this.buildFullName(member),
                    email: member.email || '',
                }
            }
        },
    }
</script>

<style scoped>
    .main {
        display: flex;
        flex-direction: column;
        padding-left: 10%;
        padding-right: 10%;
    }
    .heading {
        color: #000000;
    }
    .heading_desc {
        color: #717182;
        font-size: 16px;
        font-weight: normal;
    }
    .upload {
        width: 200px;
        height: 119px;
        margin-bottom: 8px;
    }

    .upload-box:hover {
        background: #f0f8ff;
    }
    .upload-box {
        border: 2px dashed #ccc;
        padding: 20px;
        cursor: pointer;
        width: 100%;
        min-height: 220px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: #f9f9f9;
        border-radius: 5px;
        position: relative;
    }
    .upload-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }
    input.fileInput {
        width: 100%;
        opacity: 0;
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
    }
    input.fileInput:disabled {
        pointer-events: none;
    }

    .drag-text {
        color: gray;
        font-size: 14px;
    }
    .browse {
        font-size: 20px;
        font-weight: 600;
        color: #000000;
    }
    .form-label {
        display: block;
        margin-bottom: 6px;
        font-weight: 500;
    }

    .file-input {
        display: block;
        padding: 6px;
    }

    .preview {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        width: 100%;
        padding: 10px;
        box-sizing: border-box;
    }

    .preview-item {
        position: relative;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .preview-image {
        width: 100%;
        height: 200px;
        object-fit: cover;
        border-radius: 8px;
    }

    .file-name {
        margin-top: 4px;
        max-width: 100%;
        font-size: 12px;
        color: #555;
        text-align: center;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .remove-btn {
        position: absolute;
        top: -8px;
        right: -8px;
        background: rgba(241, 33, 36, 0.9);
        color: #fff;
        border: none;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
        z-index: 2;
    }
    .success-container {
        padding: 60px 0;
        margin-top: 10%;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .success-text {
        margin-top: 16px;
        font-size: 18px;
        font-weight: 500;
        color: #000000;
    }
</style>
